import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import type { PnrBooking, PnrDiscovery } from '~/services/FlightsService';
import type {
  PnrImportFailure,
  PnrImportFlightItem,
  PnrImportOrderRequest,
  PnrImportPaid,
} from '~/services/OrdersService';
import { formatDateSlashes } from '~/services/TimeService';

import { buildServiceFeeOrderItem } from '~/utils/businessTraveller/serviceFee';

export const LUXURY_ESCAPES_BRAND = 'luxuryescapes';
export const PNR_IMPORT_BRANDS = [LUXURY_ESCAPES_BRAND, LE_BUSINESS_TRAVELLER];
export const PNR_IMPORT_REGION = 'AU';

/** svc-flights names the journey of an imported PNR after the PNR, and svc-business keys flight items on it. */
const importedJourneyId = (pnr: string) => `sabre-import:${pnr}`;

/**
 * Only an unpaid imported order can be cancelled. Its PNRs are imported with a pending reservation, which is
 * confirmed once the order is paid, so a discovery whose reservation is not pending belongs to a paid order.
 */
export function canCancelPnrImport(discovery: Pick<PnrDiscovery, 'status' | 'reservation_state'>): boolean {
  return discovery.status === 'imported' && discovery.reservation_state === 'pending';
}

const roundToCents = (value: number) => Math.round(value * 100) / 100;

export interface PnrBookingTotals {
  /** The net fare plus any ancillaries already paid on the PNR. */
  providerCost: number;
  /** The markup the provider holds on the PNR above its cost; the default margin of the import. */
  providerMarkup: number;
  currency?: string;
}

export function getPnrBookingTotals(booking?: PnrBooking): PnrBookingTotals {
  const legs = booking?.bookingJourney?.legs ?? [];
  const sum = (pick: (leg: (typeof legs)[number]) => number) =>
    roundToCents(legs.reduce((total, leg) => total + pick(leg), 0));
  return {
    providerCost: sum((leg) => (leg.price?.total?.providerCost ?? 0) + (leg.price?.total?.addonsCost ?? 0)),
    providerMarkup: sum((leg) => leg.price?.total?.markup ?? 0),
    currency: legs[0]?.price?.total?.currencyCode,
  };
}

/** The margin the agent typed: an amount of zero or more with at most two decimals, or undefined. */
export function parseMargin(margin: string): number | undefined {
  const value = Number(margin);
  if (margin.trim() === '' || !Number.isFinite(value) || value < 0 || roundToCents(value) !== value) {
    return undefined;
  }
  return value;
}

/** What the customer pays for a PNR: the provider cost plus the agent's margin and the seat and baggage handling fees. */
export function calculateCustomerPrice(providerCost: number, margin: number, handlingFeeTotal = 0): number {
  return roundToCents(providerCost + margin + handlingFeeTotal);
}

/** A seat or extra baggage of the PNR: the airline charges its price, and the agent can add a handling fee. */
type PnrAncillary = NonNullable<PnrBooking['ancillaries']>[number];
export type PnrPaidSeat = Extract<PnrAncillary, { type: 'SEAT' }>;
export type PnrBaggage = Extract<PnrAncillary, { type: 'BAGGAGE' }>;

/** The seats booked on the PNR, which get a handling fee the agent can set. */
export function getPnrPaidSeats(booking?: PnrBooking): Array<PnrPaidSeat> {
  return (booking?.ancillaries ?? []).filter((ancillary): ancillary is PnrPaidSeat => ancillary.type === 'SEAT');
}

/** The seats and extra baggage booked on the PNR, which get a handling fee the agent can set. */
export function getPnrAncillaries(booking?: PnrBooking): Array<PnrAncillary> {
  return booking?.ancillaries ?? [];
}

/** The extra baggage booked on the PNR, which gets a handling fee the agent can set. */
export function getPnrBaggages(booking?: PnrBooking): Array<PnrBaggage> {
  return (booking?.ancillaries ?? []).filter((ancillary): ancillary is PnrBaggage => ancillary.type === 'BAGGAGE');
}

/**
 * The handling fee the agent typed for a seat or extra baggage: an amount of zero or more with at most two decimals;
 * blank is zero.
 */
export function parseHandlingFee(fee: string | undefined): number | undefined {
  if (fee === undefined || fee.trim() === '') {
    return 0;
  }
  const value = Number(fee);
  if (!Number.isFinite(value) || value < 0 || roundToCents(value) !== value) {
    return undefined;
  }
  return value;
}

/**
 * The handling fees of a PNR, keyed by the Sabre ancillary id of the seat or baggage, as typed. Sabre ids are unique
 * across the ancillaries of a booking, so seats and baggage share one map.
 */
export type HandlingFees = Record<string, string>;

export function areValidHandlingFees(handlingFees: HandlingFees): boolean {
  return Object.values(handlingFees).every((fee) => parseHandlingFee(fee) !== undefined);
}

const FLIGHT_TIME_LENGTH = 5;

/** A flight time at its airport, the date then the local time, like "PER 20/11/2026 09:30". */
export function formatFlightStop(airport?: string, date?: string, time?: string): string {
  if (!date) {
    return airport ?? '';
  }
  return [airport, formatDateSlashes(date), (time ?? '').slice(0, FLIGHT_TIME_LENGTH)].filter(Boolean).join(' ');
}

/**
 * The flight of a seat or baggage as the seat map names it, the marketing airline and the number like "VA-686". The
 * airline comes from the journey flight the ancillary belongs to; without it the bare flight number is shown.
 */
export function formatAncillaryFlight(
  ancillary: Pick<PnrAncillary, 'flightId' | 'flightNumber'>,
  booking?: PnrBooking,
): string {
  const flights = booking?.bookingJourney?.legs?.flatMap((leg) => leg.flights ?? []) ?? [];
  const airlineCode = flights.find((flight) => flight.id === ancillary.flightId)?.marketingAirline?.code;
  if (!airlineCode) {
    return ancillary.flightNumber;
  }
  return `${airlineCode}-${ancillary.flightNumber}`;
}

/** The same handling fee on every given seat or baggage, keyed by ancillary id. */
export function applyHandlingFeeToAll(ancillaries: Array<Pick<PnrAncillary, 'id'>>, fee: string): HandlingFees {
  return Object.fromEntries(ancillaries.map((ancillary) => [ancillary.id, fee]));
}

/** What the customer pays for a seat or baggage: the airline price plus the handling fee. */
export function calculateAncillaryCustomerPrice(
  ancillary: Pick<PnrAncillary, 'price'>,
  fee: string | undefined,
): number {
  return roundToCents(ancillary.price.amount + (parseHandlingFee(fee) ?? 0));
}

export function calculateHandlingFeeTotal(handlingFees: HandlingFees): number {
  return roundToCents(Object.values(handlingFees).reduce((total, fee) => total + (parseHandlingFee(fee) ?? 0), 0));
}

/**
 * The fees svc-order receives for the given seats and baggage: only those given a fee above zero, none when there is
 * no fee at all.
 */
export function buildHandlingFees(
  handlingFees: HandlingFees,
  ancillaries: Array<Pick<PnrAncillary, 'id'>>,
): PnrImportFlightItem['ancillary_handling_fees'] {
  const fees = ancillaries.flatMap((ancillary) => {
    const amount = parseHandlingFee(handlingFees[ancillary.id]);
    if (!amount) {
      return [];
    }
    return [{ ancillary_id: ancillary.id, amount }];
  });
  if (!fees.length) {
    return undefined;
  }
  return fees;
}

export const INVALID_HANDLING_FEE_MESSAGE = 'An amount of zero or more with at most two decimals';

/** How many bags an extra baggage covers, like "1 extra bag" or "2 extra bags". */
export function formatBaggageQuantity(quantity: number): string {
  if (quantity === 1) {
    return '1 extra bag';
  }
  return `${quantity} extra bags`;
}

/** What the provider cost of a PNR covers: its fare, plus the ancillaries it has. */
export function getProviderCostLabel(booking?: PnrBooking): string {
  if (getPnrPaidSeats(booking).length > 0 || getPnrBaggages(booking).length > 0) {
    return 'Provider cost (fare + ancillaries)';
  }
  return 'Provider cost';
}

/** A margin is valid when it is an amount of zero or more and the customer price it gives is above zero. */
export function isValidMargin(margin: string, providerCost: number): boolean {
  const value = parseMargin(margin);
  return value !== undefined && calculateCustomerPrice(providerCost, value) > 0;
}

/** The longest payment reference svc-order accepts. */
export const PAID_REFERENCE_MAX_LENGTH = 200;

/** The paid marker the agent chose: an unchecked box sends none, and a blank reference is left out. */
export function buildPaidMarker(isPaid: boolean, reference: string): PnrImportPaid | undefined {
  if (!isPaid) {
    return undefined;
  }
  const trimmedReference = reference.trim();
  if (!trimmedReference) {
    return {};
  }
  return { reference: trimmedReference };
}

/** What the customer is asked to pay for the order: nothing when it was already paid. */
export function calculateAmountToPay(prices: Array<number>, isPaid: boolean): number {
  if (isPaid) {
    return 0;
  }
  return roundToCents(prices.reduce((total, price) => total + price, 0));
}

export interface PnrImportSelection {
  pnr: string;
  transactionKey: string;
  price: number;
  handlingFees?: PnrImportFlightItem['ancillary_handling_fees'];
}

export function buildPnrImportOrderRequest(params: {
  orderTransactionKey: string;
  brand: string;
  currency: string;
  customerId: string;
  businessId?: string;
  pnrs: Array<PnrImportSelection>;
  serviceFee?: App.LebtCustomOfferServiceFee;
  paid?: PnrImportPaid;
}): PnrImportOrderRequest {
  const serviceFeeItem = buildServiceFeeOrderItem({ lebt_service_fee: params.serviceFee });
  return {
    transaction_key: params.orderTransactionKey,
    brand: params.brand,
    region_code: PNR_IMPORT_REGION,
    currency_code: params.currency,
    customer_id: params.customerId,
    ...(params.businessId ? { business_id: params.businessId } : {}),
    ...(params.paid && { paid: params.paid }),
    items: [
      ...params.pnrs.map((selection) => ({
        type: 'flight' as const,
        pnr: selection.pnr,
        transaction_key: selection.transactionKey,
        price: selection.price,
        ...(selection.handlingFees && { ancillary_handling_fees: selection.handlingFees }),
      })),
      ...(serviceFeeItem ? [serviceFeeItem] : []),
    ],
  };
}

export interface LebtTravellerSelection {
  firstName: string;
  lastName: string;
  title?: string;
  /** The business profile the agent picked; a traveller without one is imported as a guest. */
  employeeId?: string;
}

/**
 * The svc-business import request: its own order records, wrapping the svc-order import request.
 * Business Traveller imports go through svc-business, which stores the booking manager and the traveller
 * profiles before calling svc-order; they are never sent to svc-order directly.
 */
export interface LebtPnrImportRequest {
  transactionKey: string;
  bookingManagerEmployeeId: string;
  items: Array<{
    transactionKey: string;
    type: 'flight' | 'service_fee';
    travellers: Array<{
      employeeId?: string;
      guestTravellerData: { firstName: string; lastName: string; title?: string };
      isPrimaryTraveller: boolean;
    }>;
  }>;
  orderPayload: PnrImportOrderRequest;
}

export function buildLebtPnrImportRequest(params: {
  orderPayload: PnrImportOrderRequest;
  bookingManagerEmployeeId: string;
  travellersByPnr: Record<string, Array<LebtTravellerSelection>>;
}): LebtPnrImportRequest {
  return {
    transactionKey: params.orderPayload.transaction_key,
    bookingManagerEmployeeId: params.bookingManagerEmployeeId,
    items: params.orderPayload.items.map((item) => {
      if (item.type === 'flight') {
        return {
          transactionKey: `${item.transaction_key}--${importedJourneyId(item.pnr)}`,
          type: 'flight' as const,
          travellers: (params.travellersByPnr[item.pnr] ?? []).map((traveller, index) => ({
            ...(traveller.employeeId ? { employeeId: traveller.employeeId } : {}),
            guestTravellerData: {
              firstName: traveller.firstName,
              lastName: traveller.lastName,
              title: traveller.title,
            },
            isPrimaryTraveller: index === 0,
          })),
        };
      }
      return { transactionKey: item.transaction_key, type: 'service_fee' as const, travellers: [] };
    }),
    orderPayload: params.orderPayload,
  };
}

/** The PNRs svc-order could not import, each with the reason svc-flights gave. */
export function getPnrImportFailures(error: unknown): Array<PnrImportFailure> {
  const responseBody = (error as { responseBody?: { code?: string; params?: { failures?: Array<PnrImportFailure> } } })
    ?.responseBody;
  if (responseBody?.code !== 'FLIGHT_PNR_IMPORT_FAILED') {
    return [];
  }
  return responseBody.params?.failures ?? [];
}
